import { api } from './api';

export interface ExpiringDocument {
  id: string;
  type: 'certification' | 'insurance';
  name: string;
  expiry_date: string;
  days_remaining: number;
  user_id: string;
}

export const checkExpiringDocuments = async (userId: string): Promise<ExpiringDocument[]> => {
  const expiringDocs: ExpiringDocument[] = [];
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  try {
    const [certifications, insurance] = await Promise.all([
      api.get('/certifications', { user_id: userId }),
      api.get('/insurance_policies', { user_id: userId })
    ]);

    // Filter certifications that expire within 30 days
    const expiringCerts = (certifications || []).filter((cert: any) => {
      if (!cert.expiry_date) return false;
      const expiryDate = new Date(cert.expiry_date);
      return expiryDate <= thirtyDaysFromNow;
    });

    if (expiringCerts) {
      expiringCerts.forEach((cert: any) => {
        const daysRemaining = Math.ceil(
          (new Date(cert.expiry_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        expiringDocs.push({
          id: cert.id,
          type: 'certification',
          name: cert.name,
          expiry_date: cert.expiry_date,
          days_remaining: daysRemaining,
          user_id: cert.user_id
        });
      });
    }

    // Filter insurance policies that expire within 30 days
    const expiringInsurance = (insurance || []).filter((policy: any) => {
      if (!policy.expiry_date) return false;
      const expiryDate = new Date(policy.expiry_date);
      return expiryDate <= thirtyDaysFromNow;
    });

    if (expiringInsurance) {
      expiringInsurance.forEach((policy: any) => {
        const daysRemaining = Math.ceil(
          (new Date(policy.expiry_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        expiringDocs.push({
          id: policy.id,
          type: 'insurance',
          name: `${policy.policy_type} - ${policy.provider}`,
          expiry_date: policy.expiry_date,
          days_remaining: daysRemaining,
          user_id: policy.user_id
        });
      });
    }

    expiringDocs.sort((a, b) => a.days_remaining - b.days_remaining);
    return expiringDocs;
  } catch (error) {
    console.error('Error checking expiring documents:', error);
    return [];
  }
};

export const createExpirationNotification = async (
  userId: string,
  document: ExpiringDocument
): Promise<boolean> => {
  try {
    const title = document.days_remaining <= 0
      ? `${document.name} has expired`
      : `${document.name} expiring soon`;

    const message = document.days_remaining <= 0
      ? `Your ${document.type} expired ${Math.abs(document.days_remaining)} days ago. Please renew immediately.`
      : `Your ${document.type} expires in ${document.days_remaining} days. Renew soon to maintain compliance.`;

    await api.post('/notifications', {
      user_id: userId,
      type: 'expiry',
      title,
      message,
      link: document.type === 'certification' ? '/insurance?tab=certifications' : '/insurance',
      read: false
    });
    return true;
  } catch (error) {
    console.error('Error creating notification:', error);
    return false;
  }
};

export const checkAndNotifyExpirations = async (userId: string): Promise<number> => {
  const expiringDocs = await checkExpiringDocuments(userId);
  let notificationCount = 0;

  const criticalDocs = expiringDocs.filter(doc =>
    doc.days_remaining <= 7 || doc.days_remaining < 0
  );

  const existingNotificationsRes = await api.get('/notifications', {
    user_id: userId,
    type: 'expiry',
    read: false
  });
  const existingNotifications = { data: existingNotificationsRes || [] };

  for (const doc of criticalDocs) {
    const alreadyNotified = existingNotifications.data?.some(notif =>
      notif.message.includes(doc.name)
    );

    if (!alreadyNotified) {
      const created = await createExpirationNotification(userId, doc);
      if (created) notificationCount++;
    }
  }

  return notificationCount;
};

export const updateInsuranceStatuses = async (userId: string): Promise<void> => {
  try {
    const policies = await api.get('/insurance_policies', { user_id: userId });

    if (!policies) return;

    const now = new Date();
    const updates = policies.map(policy => {
      const daysRemaining = Math.ceil(
        (new Date(policy.expiry_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      let status: 'active' | 'expiring' | 'expired';
      if (daysRemaining < 0) {
        status = 'expired';
      } else if (daysRemaining <= 30) {
        status = 'expiring';
      } else {
        status = 'active';
      }

      return { id: policy.id, status };
    });

    for (const update of updates) {
      await api.patch(`/insurance_policies/${update.id}`, { status: update.status });
    }
  } catch (error) {
    console.error('Error updating insurance statuses:', error);
  }
};
